import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import { HiOutlineBell } from 'react-icons/hi2';

import { PAYROLL_APP_ID, PROJECT_MANAGEMENT_APP_ID, PURCHASES_APP_ID } from '../apps/appsRegistry';
import { pathInWorkspace, workspaceRootPath } from './odooNavUtils';
import { PROJECT_TIMESHEETS_ROUTE } from '../projects/projects/projectScopedListsNav';
import {
    fetchNotifications,
    fetchUnreadCount,
    markAllNotificationsRead,
    markNotificationRead,
    type AppNotificationItem,
} from '../api/notificationsApi';

/**
 * App-bar notification bell: unread badge, newest-first list, click to mark read and jump to the
 * record.
 *
 * Delivery model is deliberately polling, not SignalR/WebSocket: the badge is one indexed COUNT
 * (GET /api/notifications/unread-count) on an interval, and the list body is only fetched when the
 * popover opens. A socket layer would add a connection lifecycle, reconnect handling and server-side
 * hub state for a feature whose freshness requirement is "within a minute", so it is not worth the
 * scope — the mobile app is where instant delivery matters, and that goes over FCM push.
 */

/** How often the badge re-checks while the tab is visible. Paused entirely when hidden. */
const POLL_INTERVAL_MS = 60_000;
const PAGE_SIZE = 15;

/**
 * Best-effort deep link from a notification's (resourceKey, recordKey) to a web route.
 * Returns null when there is no single-record screen to open — the click then just marks it read.
 */
function resolveTargetPath(item: AppNotificationItem): string | null {
    const pmRoot = workspaceRootPath(PROJECT_MANAGEMENT_APP_ID);
    switch (item.resourceKey) {
        case 'projects':
            return pathInWorkspace(pmRoot, `/projects/${item.recordKey}`);
        case 'loans':
            return pathInWorkspace(workspaceRootPath(PAYROLL_APP_ID), `/loans/${item.recordKey}`);
        case 'payslips':
            return pathInWorkspace(workspaceRootPath(PAYROLL_APP_ID), `/payslips/${item.recordKey}`);
        case 'tasks':
            return pathInWorkspace(pmRoot, `/tasks/${item.recordKey}`);
        case 'inwardGatePasses':
            return pathInWorkspace(workspaceRootPath(PURCHASES_APP_ID), `/inwardGatePasses/${item.recordKey}`);
        // Timesheets live on a project-scoped list rather than a standalone record route, and the
        // notification does not carry the project id — so land on the list and let the PM filter.
        case 'projectTimesheets':
        case 'projectTimesheetBatches':
            return pathInWorkspace(pmRoot, PROJECT_TIMESHEETS_ROUTE);
        default:
            return null;
    }
}

function formatWhen(iso: string): string {
    const then = new Date(iso.endsWith('Z') ? iso : `${iso}Z`).getTime();
    if (Number.isNaN(then)) return '';
    const mins = Math.floor((Date.now() - then) / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(then).toLocaleDateString();
}

export const NotificationBell = () => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [unreadCount, setUnreadCount] = React.useState(0);
    const [items, setItems] = React.useState<AppNotificationItem[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [total, setTotal] = React.useState(0);
    const open = Boolean(anchorEl);

    // Badge polling. Skipped while the tab is hidden so a backgrounded window is not a standing
    // query load, and re-checked immediately on focus so a returning user sees a current count.
    React.useEffect(() => {
        let cancelled = false;

        const refresh = async () => {
            if (document.visibilityState === 'hidden') return;
            const count = await fetchUnreadCount();
            if (!cancelled) setUnreadCount(count);
        };

        void refresh();
        const timer = window.setInterval(refresh, POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', refresh);
        window.addEventListener('focus', refresh);

        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refresh);
            window.removeEventListener('focus', refresh);
        };
    }, []);

    const loadList = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchNotifications({ page: 1, perPage: PAGE_SIZE });
            setItems(res.data);
            setTotal(res.total);
            setUnreadCount(res.unreadCount);
        } catch {
            // A failed open leaves the previous list in place rather than blanking the panel.
        } finally {
            setLoading(false);
        }
    }, []);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        void loadList();
    };

    const handleClose = () => setAnchorEl(null);

    const handleItemClick = async (item: AppNotificationItem) => {
        setAnchorEl(null);
        if (item.isRead) return;
        // Optimistic: the row is already dimming as the request goes out.
        setItems(prev => prev.map(x => (x.id === item.id ? { ...x, isRead: true } : x)));
        setUnreadCount(prev => Math.max(0, prev - 1));
        try {
            const remaining = await markNotificationRead(item.id);
            setUnreadCount(remaining);
        } catch {
            void fetchUnreadCount().then(setUnreadCount);
        }
    };

    const handleMarkAllRead = async () => {
        setItems(prev => prev.map(x => ({ ...x, isRead: true })));
        setUnreadCount(0);
        try {
            await markAllNotificationsRead();
        } catch {
            void loadList();
        }
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleOpen}
                    size="small"
                    aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
                    // Explicit white rather than `color="inherit"`: the bell sits on the coloured app
                    // bar in every workspace, and inherit picks up a muted text colour in some themes.
                    sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                >
                    <Badge
                        badgeContent={unreadCount}
                        color="error"
                        max={99}
                        overlap="circular"
                        sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}
                    >
                        <HiOutlineBell size={22} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: { width: { xs: 300, sm: 380 }, maxHeight: 460, overflow: 'hidden' },
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Notifications
                    </Typography>
                    <Button
                        size="small"
                        onClick={handleMarkAllRead}
                        disabled={unreadCount === 0}
                        sx={{ textTransform: 'none', minWidth: 0 }}
                    >
                        Mark all read
                    </Button>
                </Box>
                <Divider />

                {loading && items.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={22} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box sx={{ px: 2, py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            You have no notifications yet.
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
                        {items.map(item => {
                            const to = resolveTargetPath(item);
                            const rowProps = to
                                ? ({ component: RouterLink, to } as const)
                                : ({} as const);
                            return (
                                <ListItemButton
                                    key={item.id}
                                    {...rowProps}
                                    onClick={() => void handleItemClick(item)}
                                    alignItems="flex-start"
                                    sx={{
                                        borderInlineStart: theme =>
                                            `3px solid ${item.isRead ? 'transparent' : theme.palette.primary.main}`,
                                        bgcolor: item.isRead ? 'transparent' : 'action.hover',
                                        py: 0.75,
                                    }}
                                >
                                    <ListItemText
                                        primary={item.title}
                                        secondary={
                                            <>
                                                {item.body ? (
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block' }}
                                                    >
                                                        {item.body}
                                                    </Typography>
                                                ) : null}
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="text.disabled"
                                                >
                                                    {formatWhen(item.createdAtUtc)}
                                                </Typography>
                                            </>
                                        }
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: item.isRead ? 400 : 600,
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}

                {total > items.length ? (
                    <>
                        <Divider />
                        <Box sx={{ px: 1.5, py: 0.75 }}>
                            <Typography variant="caption" color="text.secondary">
                                Showing the {items.length} most recent of {total}.
                            </Typography>
                        </Box>
                    </>
                ) : null}
            </Popover>
        </>
    );
};

export default NotificationBell;
